import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Plus } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import AddressAutocomplete from '../components/AddressAutocomplete';
import SearchSelect from '../components/SearchSelect';
import { createLoad } from '../api/loadsApi';
import { getDrivers } from '../api/driversApi';
import { searchClients, createClient } from '../api/clientsApi';
import { useToast } from '../context/ToastContext';

function CreateLoad() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { register, handleSubmit, setValue, formState: { errors } } = useForm();

  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [originDetails, setOriginDetails] = useState(null);
  const [destinationDetails, setDestinationDetails] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [calculatingDistance, setCalculatingDistance] = useState(false);

  const [selectedClient, setSelectedClient] = useState(null);
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [savingClient, setSavingClient] = useState(false);

  useEffect(() => {
    async function fetchDrivers() {
      const result = await getDrivers(1, 50);
      if (result.success) {
        setDrivers(result.drivers);
      }
    }
    fetchDrivers();
  }, []);

  useEffect(() => {
    if (
      originDetails?.lat &&
      originDetails?.lng &&
      destinationDetails?.lat &&
      destinationDetails?.lng &&
      window.google
    ) {
      setCalculatingDistance(true);

      const service = new window.google.maps.DistanceMatrixService();

      service.getDistanceMatrix(
        {
          origins: [{ lat: originDetails.lat, lng: originDetails.lng }],
          destinations: [{ lat: destinationDetails.lat, lng: destinationDetails.lng }],
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.IMPERIAL,
        },
        (response, status) => {
          setCalculatingDistance(false);

          if (status === 'OK') {
            const result = response.rows[0]?.elements[0];
            if (result?.status === 'OK') {
              setValue('distance', result.distance.text);
            }
          }
        }
      );
    }
  }, [originDetails, destinationDetails, setValue]);

  const handleSearchClients = useCallback(async (query) => {
    return await searchClients(query);
  }, []);

  const handleQuickAddClient = async () => {
    if (!newClientName.trim()) {
      showToast('error', 'Company name is required.');
      return;
    }

    setSavingClient(true);
    try {
      const result = await createClient({
        name: newClientName,
        email: newClientEmail,
        phone: newClientPhone,
      });
      if (result.success) {
        showToast('success', 'Company added.');
        setSelectedClient({ id_client: result.client_id, name: newClientName });
        setShowAddClient(false);
        setNewClientName('');
        setNewClientEmail('');
        setNewClientPhone('');
      } else {
        showToast('error', result.message || 'Failed to add company.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    } finally {
      setSavingClient(false);
    }
  };

  const onSubmit = async (data) => {
    if (!origin || !destination) {
      showToast('error', 'Please select both origin and destination addresses.');
      return;
    }

    if (!selectedClient) {
      showToast('error', 'Please select or add the client company for this load.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await createLoad({
        client_id: selectedClient.id_client,
        origin,
        destination,
        distance: data.distance,
        price_value: Number(data.price_value),
        price_currency: data.price_currency || 'USD',
        driver_assigned_id: data.driver_assigned_id || null,
        pick_up_date: data.pick_up_date,
        delivery_date: data.delivery_date,
        origin_details: originDetails,
        destination_details: destinationDetails,
        vehicle_name: data.vehicle_name,
        vehicle_model: data.vehicle_model,
        vin_number: data.vin_number,
      });

      if (result.success) {
        showToast('success', 'Load created successfully.');
        navigate(`/loads/${result.load_id}`);
      } else {
        showToast('error', result.message || 'Failed to create load.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <TopBar title="Create Load" />

        <div style={{ padding: 24, maxWidth: 560 }}>
          <form onSubmit={handleSubmit(onSubmit)} style={styles.card}>
            <SearchSelect
              label="Client Company"
              value={selectedClient}
              onSelect={setSelectedClient}
              fetchResults={handleSearchClients}
              placeholder="Search for a client company"
              renderLabel={(client) => client.name}
              renderOption={(client) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{client.name}</div>
                  {client.email && <div style={{ fontSize: 11, color: '#9ca3af' }}>{client.email}</div>}
                </div>
              )}
            />

            {!showAddClient ? (
              <button type="button" onClick={() => setShowAddClient(true)} style={styles.addClientLink}>
                <Plus size={14} style={{ marginRight: 4 }} />
                Can't find the company? Add a new one
              </button>
            ) : (
              <div style={styles.quickAddBox}>
                <label style={styles.label}>Company Name</label>
                <input
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  style={styles.input}
                  placeholder="Company name"
                />
                <label style={styles.label}>Email (optional)</label>
                <input
                  value={newClientEmail}
                  onChange={(e) => setNewClientEmail(e.target.value)}
                  style={styles.input}
                  placeholder="company@example.com"
                />
                <label style={styles.label}>Phone (optional)</label>
                <input
                  value={newClientPhone}
                  onChange={(e) => setNewClientPhone(e.target.value)}
                  style={styles.input}
                  placeholder="Phone number"
                />
                <div style={styles.buttonRow}>
                  <button type="button" onClick={() => setShowAddClient(false)} style={styles.cancelBtn}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleQuickAddClient}
                    disabled={savingClient}
                    style={styles.submitBtn}
                  >
                    {savingClient ? '...' : 'Add Company'}
                  </button>
                </div>
              </div>
            )}

            <div style={{ marginTop: 20 }}>
              <AddressAutocomplete
                label="Origin (Pickup Address)"
                value={origin}
                onChange={setOrigin}
                onAddressSelect={setOriginDetails}
              />
            </div>

            <div style={{ marginTop: 16 }}>
              <AddressAutocomplete
                label="Destination (Dropoff Address)"
                value={destination}
                onChange={setDestination}
                onAddressSelect={setDestinationDetails}
              />
            </div>

            <label style={styles.label}>
              Distance{' '}
              {calculatingDistance && (
                <span style={{ color: '#9ca3af', fontWeight: 400 }}>(calculating...)</span>
              )}
            </label>
            <input {...register('distance')} style={styles.input} />

            <label style={styles.label}>Price</label>
            <input
              {...register('price_value', { required: true })}
              type="number"
              step="0.01"
              style={styles.input}
            />
            {errors.price_value && <p style={styles.errorText}>Required</p>}

            <label style={styles.label}>Currency</label>
            <select {...register('price_currency')} style={styles.input} defaultValue="USD">
              <option value="USD">USD</option>
              <option value="CAD">CAD</option>
            </select>

            <label style={styles.label}>Assign Driver</label>
            <select {...register('driver_assigned_id')} style={styles.input}>
              <option value="">Unassigned</option>
              {drivers.map((d) => (
                <option key={d.id_driver} value={d.id_driver}>
                  {d.driver_name}
                </option>
              ))}
            </select>

            <label style={styles.label}>Pick Up Date</label>
            <input
              {...register('pick_up_date', { required: true })}
              type="date"
              style={styles.input}
            />
            {errors.pick_up_date && <p style={styles.errorText}>Required</p>}

            <label style={styles.label}>Delivery Date</label>
            <input
              {...register('delivery_date', { required: true })}
              type="date"
              style={styles.input}
            />
            {errors.delivery_date && <p style={styles.errorText}>Required</p>}

            <div style={styles.divider} />
            <div style={styles.sectionTitle}>Vehicle</div>
            <p style={styles.sectionHint}>
              A load can't be created without at least one vehicle. You can add more vehicles later from the load's detail page.
            </p>

            <label style={styles.label}>Vehicle Make</label>
            <input
              {...register('vehicle_name', { required: true })}
              placeholder="e.g. Toyota"
              style={styles.input}
            />
            {errors.vehicle_name && <p style={styles.errorText}>Required</p>}

            <label style={styles.label}>Vehicle Model</label>
            <input
              {...register('vehicle_model', { required: true })}
              placeholder="e.g. Camry"
              style={styles.input}
            />
            {errors.vehicle_model && <p style={styles.errorText}>Required</p>}

            <label style={styles.label}>VIN Number</label>
            <input
              {...register('vin_number', { required: true })}
              placeholder="17-character VIN"
              style={styles.input}
            />
            {errors.vin_number && <p style={styles.errorText}>Required</p>}

            <div style={styles.buttonRow}>
              <button type="button" onClick={() => navigate('/loads')} style={styles.cancelBtn}>
                Cancel
              </button>
              <button type="submit" disabled={submitting} style={styles.submitBtn}>
                {submitting ? '...' : 'Create Load'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

const styles = {
  card: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 24,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 6,
    marginTop: 16,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  errorText: {
    color: '#dc2626',
    fontSize: 12,
    marginTop: 4,
  },
  addClientLink: {
    display: 'flex',
    alignItems: 'center',
    background: 'none',
    border: 'none',
    color: '#2563eb',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '8px 0 0',
  },
  quickAddBox: {
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: 16,
    marginTop: 10,
  },
  divider: {
    height: 1,
    backgroundColor: '#f3f4f6',
    margin: '24px 0 4px',
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#374151',
    textTransform: 'uppercase',
    marginTop: 8,
  },
  sectionHint: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 4,
    marginBottom: 0,
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 24,
  },
  cancelBtn: {
    padding: '10px 20px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    backgroundColor: 'white',
    color: '#374151',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '10px 20px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#2563eb',
    color: 'white',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
  },
};

export default CreateLoad;